import { db } from "@/firebase/config"
import {
  Timestamp,
  Unsubscribe,
  addDoc,
  collection,
  doc,
  getDoc,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from "firebase/firestore"

const COLLECTION_NAME = "UserNotifications"
const collectionRef = collection(db, COLLECTION_NAME)

export type UserNotificationType = "chat" | "property"

export type UserNotification = {
  id: string
  userId: string
  type: UserNotificationType
  title?: string | null
  body?: string | null
  read: boolean
  createdAt?: Timestamp | null
  createdAtMs?: number | null
  metadata?: Record<string, unknown> | null
}

export type CreateUserNotificationInput = {
  userId: string
  type: UserNotificationType
  title?: string | null
  body?: string | null
  metadata?: Record<string, unknown> | null
  id?: string | null
}

const normalizeUserId = (userId: string) => userId.trim().toLowerCase()

export const createUserNotification = async (input: CreateUserNotificationInput) => {
  const userIdNorm = normalizeUserId(input.userId)
  const payload: Record<string, unknown> = {
    userId: userIdNorm,
    type: input.type,
    title: input.title ?? null,
    body: input.body ?? null,
    metadata: input.metadata ?? null,
    read: false,
    createdAt: serverTimestamp(),
    createdAtMs: Date.now()
  }

  if (input.id) {
    const docRef = doc(collectionRef, input.id)
    const existing = await getDoc(docRef)
    if (existing.exists()) {
      await updateDoc(docRef, {
        type: input.type,
        title: input.title ?? existing.data().title ?? null,
        body: input.body ?? existing.data().body ?? null,
        metadata: input.metadata ?? existing.data().metadata ?? null
      })
      return { id: docRef.id }
    }
    await setDoc(docRef, payload, { merge: false })
    return { id: docRef.id }
  }

  const docRef = await addDoc(collectionRef, payload)
  return { id: docRef.id }
}

export const subscribeToUserNotifications = (
  userId: string,
  callback: (notifications: UserNotification[]) => void,
  options?: { limit?: number }
): Unsubscribe => {
  const limitCount = options?.limit ?? 50
  const userIdNorm = normalizeUserId(userId)
  const q = query(collectionRef, where("userId", "==", userIdNorm), orderBy("createdAt", "desc"), fsLimit(limitCount))
  return onSnapshot(q, (snapshot) => {
    const notifications = snapshot.docs.map((docSnap) => {
      const data = docSnap.data()
      return {
        id: docSnap.id,
        userId: data.userId,
        type: data.type,
        title: data.title ?? null,
        body: data.body ?? null,
        read: Boolean(data.read),
        createdAt: data.createdAt ?? null,
        createdAtMs: typeof data.createdAtMs === "number" ? data.createdAtMs : null,
        metadata: data.metadata ?? null
      } as UserNotification
    })
    callback(notifications)
  })
}

export const markNotificationRead = async (notificationId: string) => {
  if (!notificationId) return
  const docRef = doc(collectionRef, notificationId)
  await updateDoc(docRef, { read: true, readAt: serverTimestamp() })
}

export const markNotificationsRead = async (notificationIds: string[]) => {
  const unique = Array.from(new Set(notificationIds.filter(Boolean)))
  if (unique.length === 0) return
  const updates = unique.map((id) => markNotificationRead(id))
  await Promise.all(updates)
}
